import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Form, InputNumber, Spin } from 'antd';
import { get, put } from '../../../api/client';

interface OrderPolicies {
  maxSlotEdits: number;
  cancelRiskWindowDays: number;
  cancelRiskThreshold: number;
  defaults: {
    maxSlotEdits: number;
    cancelRiskWindowDays: number;
    cancelRiskThreshold: number;
  };
}

/**
 * 订单策略面板：档期修改次数上限 + 违约风险提示的窗口与阈值。
 *
 * 两条都存服务端 KV（与补录策略同一形状），改动只影响之后的判定、不回写历史，
 * 所以这里不需要「先停用它再改」之类的编排——改完下一个请求就按新值走。
 * 表单在数据到手之后才挂载：antd 的 `initialValues` 只在首次挂载生效，
 * 先渲染空表单会把默认值冻在界面上，改完保存也看不出库里其实是什么。
 */
export function OrderPolicyPanel() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin-order-policy'],
    queryFn: () => get<OrderPolicies>('/admin/order-policy'),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin-order-policy'] });

  const slotForm = Form.useForm()[0];
  const riskForm = Form.useForm()[0];

  const saveSlotEdit = useMutation({
    mutationFn: (maxSlotEdits: number) => put('/admin/order-policy/slot-edit', { max_slot_edits: maxSlotEdits }),
    onSuccess: (_data, maxSlotEdits) => {
      message.success(`档期修改次数上限已设为 ${maxSlotEdits} 次`);
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const saveCancelRisk = useMutation({
    mutationFn: (values: { windowDays: number; threshold: number }) =>
      put('/admin/order-policy/cancel-risk', {
        window_days: values.windowDays,
        threshold: values.threshold,
      }),
    onSuccess: (_data, values) => {
      message.success(`取消风险提示已设为 ${values.windowDays} 天内 ${values.threshold} 次`);
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  if (query.isLoading) return <Spin />;

  const defaults = query.data?.defaults;

  return (
    <>
      <Card title="档期修改次数上限" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="为什么预约日期不可改、其余档期可改且有次数上限"
          description="预约日期提交后不可更改：要换日子只能取消这张单再重新提交，取消记录留在状态日志里，改期这件事本身就是留痕。结束日期、全天/时间段、到达时间照旧可改（现场延期是常态），但一张单累计只允许改这么多次，超限后同样只能取消重提。只在这一次保存真的动了档期时才计数——补备注、登记回款不消耗额度。"
        />
        <Form
          form={slotForm}
          layout="inline"
          initialValues={{ maxSlotEdits: query.data?.maxSlotEdits }}
          onFinish={(values: { maxSlotEdits: number }) => saveSlotEdit.mutate(values.maxSlotEdits)}
        >
          <Form.Item
            name="maxSlotEdits"
            label="每张单最多可修改档期"
            rules={[{ required: true, message: '请输入次数' }]}
          >
            <InputNumber min={1} max={100} precision={0} addonAfter="次" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saveSlotEdit.isPending}>
              保存
            </Button>
          </Form.Item>
          {defaults && query.data?.maxSlotEdits !== defaults.maxSlotEdits && (
            <Form.Item>
              <Button type="link" onClick={() => slotForm.setFieldsValue({ maxSlotEdits: defaults.maxSlotEdits })}>
                恢复默认（{defaults.maxSlotEdits} 次）
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>

      <Card title="违约风险提示（取消留痕）">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="审核一张单时，服务端回看提交方近期的取消记录"
          description={'取消的订单不删除，就是为了这一刻能回答「这个人最近放了几次鸽子」。达到阈值时，接单方在审核面板上会看到一条风险提示（三端一致）。' +
            '「取消后又有新单承接」的那几次不计入——改期重开一张是正常操作，不算违约。低于阈值时提示条整块不渲染，不占审核面板的行。'}
        />
        <Form
          form={riskForm}
          layout="inline"
          initialValues={{
            windowDays: query.data?.cancelRiskWindowDays,
            threshold: query.data?.cancelRiskThreshold,
          }}
          onFinish={(values: { windowDays: number; threshold: number }) => saveCancelRisk.mutate(values)}
        >
          <Form.Item name="windowDays" label="回看窗口" rules={[{ required: true, message: '请输入天数' }]}>
            <InputNumber min={1} max={365} precision={0} addonAfter="天" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="threshold" label="取消达" rules={[{ required: true, message: '请输入次数' }]}>
            <InputNumber min={1} max={100} precision={0} addonAfter="次提示" style={{ width: 170 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saveCancelRisk.isPending}>
              保存
            </Button>
          </Form.Item>
          {defaults && (query.data?.cancelRiskWindowDays !== defaults.cancelRiskWindowDays || query.data?.cancelRiskThreshold !== defaults.cancelRiskThreshold) && (
            <Form.Item>
              <Button
                type="link"
                onClick={() =>
                  riskForm.setFieldsValue({
                    windowDays: defaults.cancelRiskWindowDays,
                    threshold: defaults.cancelRiskThreshold,
                  })
                }
              >
                恢复默认（{defaults.cancelRiskWindowDays} 天内 {defaults.cancelRiskThreshold} 次）
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>
    </>
  );
}
